import { nanoid } from 'nanoid';

/**
 * Check if operator is unary (no values)
 * @param {string} op - Operator
 * @returns {boolean}
 */
export function isUnaryOperator(op) {
  return op === 'isEmpty' || op === 'isNotEmpty';
}

/**
 * Check if operator is list (multiple values)
 * @param {string} op - Operator
 * @returns {boolean}
 */
export function isListOperator(op) {
  return op === 'in' || op === 'notIn';
}

/**
 * Check if operator is binary (single value)
 * @param {string} op - Operator
 * @returns {boolean}
 */
export function isBinaryOperator(op) {
  return !isUnaryOperator(op) && !isListOperator(op);
}

/**
 * Get field metadata from schema (helper for normalization)
 * 
 * @param {string} fieldName - Field name
 * @param {Object} schema - Query schema
 * @returns {Object|null} Field metadata
 */
function getFieldMetadata(fieldName, schema) {
  if (!schema || !schema.fields) return null;
  return schema.fields.find(f => f.name === fieldName) || null;
}

// normalizeFilterPill deleted - normalization happens only in filterPillsToAST

/**
 * Validate filter pill structure and types
 * 
 * @param {Object} pill - Filter pill to validate
 * @param {Object} schema - Query schema (optional, for field type detection)
 * @throws {Error} If pill structure is invalid
 */
export function validateFilterPill(pill, schema = null) {
  if (!pill.id || typeof pill.id !== 'string') {
    throw new Error('Filter pill must have a string id');
  }
  
  if (!pill.field || typeof pill.field !== 'string') {
    throw new Error('Filter pill must have a string field');
  }
  
  if (!pill.operator || typeof pill.operator !== 'string') {
    throw new Error('Filter pill must have a string operator');
  }
  
  const { operator, values } = pill;
  const fieldMeta = schema ? getFieldMetadata(pill.field, schema) : null;
  const isDateField = fieldMeta?.kind === 'date';
  
  // Helper to validate a single value
  const isValidValue = (value) => {
    // Date objects are never allowed
    if (value instanceof Date) {
      return false;
    }
    
    // For date fields, MUST be {date: string, timezone: string}
    if (isDateField) {
      if (typeof value !== 'object' || value === null) {
        return false;
      }
      return value.date && typeof value.date === 'string' && 
             value.timezone && typeof value.timezone === 'string' &&
             /^\d{4}-\d{2}-\d{2}$/.test(value.date);
    }
    
    // Standard types: string or number
    return typeof value === 'string' || typeof value === 'number';
  };
  
  if (isUnaryOperator(operator)) {
    // Unary: no values allowed
    if (values !== undefined && values !== null && values.length > 0) {
      throw new Error(`Unary operator ${operator} cannot have values`);
    }
  } else if (isListOperator(operator)) {
    // List: multiple values required
    if (!Array.isArray(values) || values.length === 0) {
      throw new Error(`List operator ${operator} requires at least one value`);
    }
    
    // Validate value types
    values.forEach((value, index) => {
      if (!isValidValue(value)) {
      if (value instanceof Date) {
          throw new Error(`Filter pill value at index ${index} is a Date object. Use {date: string, timezone: string} instead.`);
        }
        if (isDateField) {
          throw new Error(`Filter pill value at index ${index} must be {date: string, timezone: string} for date fields`);
      }
        throw new Error(`Filter pill value at index ${index} must be string or number, got ${typeof value}`);
      }
    });
  } else {
    // Binary: single value required
    if (!Array.isArray(values) || values.length !== 1) {
      throw new Error(`Binary operator ${operator} requires exactly one value`);
    }
    
    // Validate value type
    const value = values[0];
    if (!isValidValue(value)) {
    if (value instanceof Date) {
        throw new Error('Filter pill value is a Date object. Use {date: string, timezone: string} instead.');
      }
      if (isDateField) {
        throw new Error('Filter pill value must be {date: string, timezone: string} for date fields');
    }
      throw new Error(`Filter pill value must be string or number, got ${typeof value}`);
    }
  }
  
  return true;
}

/**
 * Ensure filter pill has id
 * 
 * @param {Object} pill - Filter pill
 * @returns {Object} Filter pill with id
 */
export function ensureFilterPillId(pill) {
  if (pill.id) {
    return pill;
  }
  return { ...pill, id: nanoid() };
}

